/**
 * Copyright Motili Inc., 2023 All Rights Reserved
 */

import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
} from 'react-router';
import * as Sentry from '@sentry/react';
import { pdfjs } from 'react-pdf';
import { LicenseInfo } from '@mui/x-license';
import * as amplitude from '@amplitude/analytics-browser';

import { ServiceWorkerProvider } from 'useServiceWorker';
import { ACCOUNT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import * as AuthService from 'common/services/AuthService';
import Auth0ProviderWithConfig from 'Auth0ProviderWithConfig';
import Config from 'Config';
import App from './App';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// vite preload error listener handled in useServiceWorker.jsx: handleVitePreloadError
globalThis.addEventListener('vite:preloadError', e => {
    e.preventDefault();
});

LicenseInfo.setLicenseKey(Config.muiXLicenseKey);

const sentryConfig = {
    dsn: Config.sentry.dsn,
    sendDefaultPii: true,
    integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
            // traceXHR and traceFetch were disabled in v7 — revisit if HTTP spans appear in Sentry
        }),
    ],
    // Set tracesSampleRate to 0.0 in production to avoid performance overhead
    tracesSampleRate: Config.environment === 'prod' ? 0.0 : 0.1,
    release: Config.release,
    environment: Config.environment,
    beforeSend(event) {
        const doNotSendEnvironments = [
            'local',
            'dashboardlocal',
            'dev',
            'dev2',
            'dashboarddevintg',
        ];
        if (doNotSendEnvironments.includes(Config.environment)) {
            return null;
        }
        return event;
    },
};
Sentry.init(sentryConfig);

if (AuthService.isLoggedIn()) {
    const amplitudeApiKey = Config.amplitudeApiKey;
    let userType;

    const user = AuthService.getAccount();

    Sentry.setUser({
        id: user.id,
        email: user.email,
    });

    if (AuthService.isMotiliAccount()) {
        userType = ACCOUNT_TYPE.motili;
        Sentry.setExtra({
            userContext: 'ops',
        });
    }

    if (AuthService.isClientAccount()) {
        userType = ACCOUNT_TYPE.client;
        Sentry.setExtra({
            userContext: 'cli',
        });
    }

    if (AuthService.isContractorAccount()) {
        userType = ACCOUNT_TYPE.contractor;
        Sentry.setExtra({
            userContext: 'con',
        });
    }

    if (Config.environment === 'prod' && amplitudeApiKey) {
        amplitude.init(
            amplitudeApiKey,
            `${userType} - ${user.email} - ${user.id}`,
            {
                fetchRemoteConfig: true,
                autocapture: {
                    attribution: true,
                    fileDownloads: true,
                    formInteractions: true,
                    pageViews: true,
                    sessions: true,
                    elementInteractions: true,
                    networkTracking: true,
                    webVitals: true,
                    frustrationInteractions: true,
                },
            }
        );
    }
}

const container = document.getElementById('app');
const root = createRoot(container);
root.render(
    <Auth0ProviderWithConfig>
        <ServiceWorkerProvider>
            <App />
        </ServiceWorkerProvider>
    </Auth0ProviderWithConfig>
);
